/**
 * ds4 B — 전 목록·카드 공용 정렬(순수 · 2026-10-16 ds4-s). 홈·허브·캘린더·블로그·검색·미분양이 «이것만» 쓴다.
 *
 *   청약 일정   접수 시작일 오름차순 · 같은 날은 마감 임박 순 · 지난 일정(마감일 < 오늘)은 뒤로 «따로» 반환(「최근 지난 일정」 = 마감일 내림차순)
 *   분양예정    expected_sale_period 오름차순(가까운 달 먼저) — 판정은 `@/components/region-doc/period` 의 upcomingPeriod 에 위임.
 *               여기서 하는 일은 «분기·반기 표기를 첫 달 「YYYY-MM」 으로 바꿔 넘기는 것» 뿐(period.ts 는 「2026Q3」을 'raw' 로 본다).
 *               「YYYY-MM」·「YYYY.MM」·「YYYY년 M월」 → 그 달 · 「YYYYQn」·「YYYY년 n분기」 → 분기 첫 달 · 「YYYYHn」·「YYYY년 상/하반기」 → 1·7월
 *               · 「YYYY」 → 그해 12월 · 지난 예정월(일정 지연)은 맨 뒤(키 999999) · 못 읽는 원문은 그 바로 앞(999998).
 *   재개발      stage_updated_at 내림차순 · 행 라벨 「MM.DD 변동」(KST)
 *   글·미분양   발행일·갱신일 내림차순 (sortByDateDesc)
 *   지역 타일   현장 수 내림차순
 *   모든 정렬은 «안정»(같은 키는 들어온 순서 유지) · 키가 없는 항목은 뒤로 · 항목은 지우지 않는다.
 *
 * 날짜는 전부 KST 'YYYY-MM-DD'. 오늘 = todayKST(apt/subscription-status → 정본 cl-core/kst kstYmd).
 *
 * CL-SORT 게이트(tools/cl/gates/sort/run.mjs) DOM 계약 — 목록 뿌리에 `data-ds4-sort="<규칙>"`, 각 항목에 `data-ds4-sort-key="<키>"`.
 *   키는 아래 *SortKey 함수 값을 그대로 넣는다(sortRootAttrs / sortKeyAttrs 로 펼쳐 넣기). 규칙 이름은 SortRule.
 */
import { upcomingPeriod, PERIOD_DELAYED, type Period } from '@/components/region-doc/period';
import { todayKST } from '@/lib/apt/subscription-status';

/* ───────────── 공통 ───────────── */

export type Ymd = string;
export type SortRule = 'schedule' | 'schedule-past' | 'upcoming' | 'redev' | 'posts' | 'unsold' | 'tiles';

/** 섹션 헤더 정렬 라벨(12/700 #2F4B8A — SectionHead sortLabel prop 에 넣는다). */
export const SORT_LABEL: Readonly<Record<SortRule, string>> = {
  schedule: '접수일 가까운 순',
  'schedule-past': '최근 지난 일정',
  upcoming: '분양 시기 가까운 순',
  redev: '최근 단계 변동 순',
  posts: '최신 발행 순',
  unsold: '최근 갱신 순',
  tiles: '현장 많은 순',
};
/** 지난 청약 일정 묶음 제목(섹션 안 소제목). */
export const SCHEDULE_PAST_TITLE = SORT_LABEL['schedule-past'];

export const SORT_ATTR = 'data-ds4-sort';
export const SORT_KEY_ATTR = 'data-ds4-sort-key';
/** 목록 뿌리(ul·ol·tbody·grid div)에 펼쳐 넣는다: `<ul {...sortRootAttrs('schedule')}>`. */
export const sortRootAttrs = (rule: SortRule) => ({ [SORT_ATTR]: rule }) as Record<string, string>;
/** 각 항목(li·tr·카드 a)에 펼쳐 넣는다: `<li {...sortKeyAttrs(scheduleSortKey(s, e))}>`. */
export const sortKeyAttrs = (key: string) => ({ [SORT_KEY_ATTR]: key }) as Record<string, string>;

/** 오늘(KST) 'YYYY-MM-DD'. */
export const kstToday = (now: Date | number = Date.now()): Ymd => todayKST(new Date(now));

/**
 * 날짜·시각 값 → KST 'YYYY-MM-DD'. 못 읽으면 null.
 *   'YYYY-MM-DD'(date 열)·'YYYYMMDD' → 그날 그대로 · 시각+오프셋('…T…Z'·'…+00'·'…+09:00') → KST 날짜 · 오프셋 없는 시각 → 앞 10자.
 */
export function kstYmdOf(v: string | Date | null | undefined): Ymd | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? todayKST(v) : null;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const c = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (c) return `${c[1]}-${c[2]}-${c[3]}`;
  const ts = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i.exec(s);
  if (!ts) return null;
  if (!ts[3]) return ts[1];
  let off = ts[3].toUpperCase();
  if (off !== 'Z' && /^[+-]\d{2}$/.test(off)) off += ':00';
  else if (off !== 'Z' && /^[+-]\d{4}$/.test(off)) off = `${off.slice(0, 3)}:${off.slice(3)}`;
  const d = new Date(`${ts[1]}T${ts[2]}${off}`);
  return Number.isFinite(d.getTime()) ? todayKST(d) : null;
}

/** 안정 정렬(Array.prototype.sort 는 ES2019+ 안정 — 입력 배열은 건드리지 않는다). */
const stable = <T>(items: readonly T[], cmp: (a: T, b: T) => number): T[] => [...items].sort(cmp);
/** null 은 뒤로, 문자열 오름/내림. */
const cmpStr = (a: string | null, b: string | null, dir: 1 | -1): number =>
  a === b ? 0 : a == null ? 1 : b == null ? -1 : a < b ? -dir : dir;

/* ───────────── 청약 일정 ───────────── */

export interface ScheduleDates { start?: string | null; end?: string | null }

/** 지난 일정 = 마감일(없으면 시작일) < 오늘. 날짜가 하나도 없으면 지난 것으로 보지 않는다(뒤로만 간다). */
export function isSchedulePast(d: ScheduleDates, today: Ymd): boolean {
  const last = kstYmdOf(d.end ?? null) ?? kstYmdOf(d.start ?? null);
  return last != null && last < today;
}

/** 다가오는·진행 중 일정 비교: 시작일 오름차순 → 같은 날은 마감일 오름차순(임박 먼저) → 날짜 없음은 뒤. */
export function compareSchedule(a: ScheduleDates, b: ScheduleDates): number {
  return cmpStr(kstYmdOf(a.start ?? null), kstYmdOf(b.start ?? null), 1) || cmpStr(kstYmdOf(a.end ?? null), kstYmdOf(b.end ?? null), 1);
}

/**
 * 청약 일정 정렬. upcoming = 오늘 이후·진행 중(시작일 오름차순, 같은 날 마감 임박 순) · past = 「최근 지난 일정」(마감일 내림차순).
 * 화면은 upcoming 을 본 목록에, past 를 맨 뒤 별도 묶음(SCHEDULE_PAST_TITLE)에 낸다.
 */
export function splitSchedule<T>(items: readonly T[], datesOf: (x: T) => ScheduleDates, today: Ymd): { upcoming: T[]; past: T[] } {
  const up: T[] = [];
  const past: T[] = [];
  for (const it of items) (isSchedulePast(datesOf(it), today) ? past : up).push(it);
  const lastOf = (x: T) => { const d = datesOf(x); return kstYmdOf(d.end ?? null) ?? kstYmdOf(d.start ?? null); };
  return {
    upcoming: stable(up, (a, b) => compareSchedule(datesOf(a), datesOf(b))),
    past: stable(past, (a, b) => cmpStr(lastOf(a), lastOf(b), -1) || cmpStr(kstYmdOf(datesOf(a).start ?? null), kstYmdOf(datesOf(b).start ?? null), -1)),
  };
}

/** 한 줄로 이어 붙인 순서(다가오는 것 → 지난 것). */
export function sortSchedule<T>(items: readonly T[], datesOf: (x: T) => ScheduleDates, today: Ymd): T[] {
  const { upcoming, past } = splitSchedule(items, datesOf, today);
  return [...upcoming, ...past];
}

/** 게이트 키 「시작|마감」(KST 'YYYY-MM-DD', 없으면 빈칸). */
export const scheduleSortKey = (start: string | null | undefined, end: string | null | undefined): string =>
  `${kstYmdOf(start ?? null) ?? ''}|${kstYmdOf(end ?? null) ?? ''}`;

/* ───────────── 분양예정(expected_sale_period) ───────────── */

interface Coarse { y: number; q?: number; h?: 1 | 2 }
/** 분기·반기 표기만 읽는다(월·연도 판정은 period.ts 몫). */
function coarseOf(t: string): Coarse | null {
  let m = /^(\d{4})\s*Q\s*([1-4])$/i.exec(t) ?? /^(\d{4})\s*년\s*([1-4])\s*분기/.exec(t);
  if (m) return { y: Number(m[1]), q: Number(m[2]) };
  m = /^(\d{4})\s*H\s*([12])$/i.exec(t);
  if (m) return { y: Number(m[1]), h: Number(m[2]) as 1 | 2 };
  m = /^(\d{4})\s*년\s*(상|하)\s*반기/.exec(t);
  if (m) return { y: Number(m[1]), h: m[2] === '상' ? 1 : 2 };
  return null;
}
const stripTail = (raw: string | null | undefined) => String(raw ?? '').replace(/\s*분양\s*예정$/, '').trim();

/** 분기 → 첫 달(Q1 01·Q2 04·Q3 07·Q4 10) · 반기 → 01·07 의 「YYYY-MM」. 그 밖의 표기는 그대로(꼬리 「분양예정」만 제거). */
export function normalizeSalePeriod(raw: string | null | undefined): string {
  const t = stripTail(raw);
  const c = coarseOf(t);
  if (!c) return t;
  const m = c.q ? c.q * 3 - 2 : c.h === 1 ? 1 : 7;
  return `${c.y}-${String(m).padStart(2, '0')}`;
}

/**
 * 판정 = period.ts upcomingPeriod(월·연도·원문). period.ts 가 읽지 못하는 분기·반기만 여기서:
 *   정렬 키 = 첫 달(YYYYMM) · 지난 판정 = «마지막 달» < 이번 달(연도만 값이 12월이 지나야 지연인 period.ts 와 같은 원칙 —
 *   10월에 「2026년 하반기」는 아직 지연이 아니다) · 지난 것은 period.ts 와 같은 모양(「일정 지연」 + 「당초 … 예정」 · 999999).
 * 빈 값이면 null.
 */
export function salePeriodOf(raw: string | null | undefined, today: Ymd): Period | null {
  const t = stripTail(raw);
  const c = coarseOf(t);
  if (!c) return upcomingPeriod(t, today);
  const first = c.q ? c.q * 3 - 2 : c.h === 1 ? 1 : 7;
  const last = c.q ? c.q * 3 : c.h === 1 ? 6 : 12;
  const label = c.q ? `${c.y}년 ${c.q}분기` : `${c.y}년 ${c.h === 1 ? '상' : '하'}반기`;
  const nowKey = Number(today.slice(0, 4)) * 100 + Number(today.slice(5, 7));
  if (c.y * 100 + last < nowKey) return { label: PERIOD_DELAYED, kind: 'past', sub: `당초 ${label} 예정`, tone: 'delay', sortKey: 999999 };
  return { label, kind: 'raw', tone: null, sortKey: c.y * 100 + first };
}

/** 정렬 키: YYYYMM(연도만 YYYY12) · 못 읽는 원문 999998 · 지난 예정월 999999 · 빈 값 1000000(맨 끝). */
export function salePeriodSortKey(raw: string | null | undefined, today: Ymd): number {
  return salePeriodOf(raw, today)?.sortKey ?? 1_000_000;
}

/** 같은 키일 때 정밀한 표기 먼저: 월 0 · 분기/반기 1 · 연도 2 · 원문 3(지난 것·빈 값은 키가 이미 같아 순서 유지). */
function precisionRank(raw: string | null | undefined, today: Ymd): number {
  const p = salePeriodOf(raw, today);
  if (!p || p.kind === 'past') return 0;
  if (coarseOf(stripTail(raw))) return 1;
  return p.kind === 'month' ? 0 : p.kind === 'year' ? 2 : 3;
}

/** 분양예정 정렬(가까운 달 먼저 · 같은 달은 「YYYY-MM」 > 「YYYYQn」 > 「YYYY」 · 지난 예정월 맨 뒤 · 안정). */
export function sortUpcoming<T>(items: readonly T[], periodOf: (x: T) => string | null | undefined, today: Ymd): T[] {
  return stable(items, (a, b) =>
    salePeriodSortKey(periodOf(a), today) - salePeriodSortKey(periodOf(b), today) || precisionRank(periodOf(a), today) - precisionRank(periodOf(b), today));
}

/** 게이트 키 = salePeriodSortKey 문자열. */
export const upcomingSortKey = (raw: string | null | undefined, today: Ymd): string => String(salePeriodSortKey(raw, today));

export interface SalePeriodBadge { text: string; tone: 'navy' | 'delay' }
/**
 * 카드 이름 위 라벨(SiteCard periodLabel — 12/700 네이비). 정밀도는 원문만큼만(분기를 달로 «올려» 쓰지 않는다 — sale-period.ts 원칙).
 *   「2026-10」 → 「2026년 10월 예정」 · 「2026Q4」 → 「2026년 4분기 예정」 · 「2026H2」 → 「2026년 하반기 예정」 · 「2027」 → 「2027년 예정」
 *   지난 예정월 → 「일정 지연」(tone 'delay' · 색은 CSS 몫) · 못 읽는 원문 → 「{원문} 예정」 · 빈 값 → null.
 */
export function salePeriodBadge(raw: string | null | undefined, today: Ymd): SalePeriodBadge | null {
  const p = salePeriodOf(raw, today);
  if (!p) return null;
  if (p.kind === 'past') return { text: PERIOD_DELAYED, tone: 'delay' };
  const t = stripTail(raw);
  const c = coarseOf(t);
  const y = Math.floor(p.sortKey / 100);
  if (c?.q) return { text: `${c.y}년 ${c.q}분기 예정`, tone: 'navy' };
  if (c?.h) return { text: `${c.y}년 ${c.h === 1 ? '상' : '하'}반기 예정`, tone: 'navy' };
  if (p.kind === 'month') return { text: `${y}년 ${p.sortKey % 100}월 예정`, tone: 'navy' };
  if (p.kind === 'year') return { text: `${y}년 예정`, tone: 'navy' };
  return { text: /예정$/.test(t) ? t : `${t} 예정`, tone: 'navy' };
}
/** 라벨 문자열만(「2026년 10월 예정」). */
export const salePeriodLabel = (raw: string | null | undefined, today: Ymd): string | null => salePeriodBadge(raw, today)?.text ?? null;

/* ───────────── 날짜 내림차순(재개발·글·미분양) ───────────── */

/** 시각 비교 값(ms). 'YYYY-MM-DD' 는 KST 자정. 못 읽으면 null. */
function msOf(v: string | Date | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  const s = String(v).trim();
  const ymd = kstYmdOf(s);
  if (!ymd) return null;
  if (/^\d{4}-?\d{2}-?\d{2}$/.test(s) || !/(Z|[+-]\d{2}(:?\d{2})?)$/i.test(s)) {
    const ms = Date.parse(`${ymd}T00:00:00+09:00`);
    return Number.isFinite(ms) ? ms : null;
  }
  const ms = Date.parse(s.replace(' ', 'T').replace(/\s+(?=[+-]\d{2})/, '').replace(/([+-]\d{2})$/, '$1:00').replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
  return Number.isFinite(ms) ? ms : null;
}

/** 최신 먼저(안정 · 날짜 없음 뒤). 재개발 stage_updated_at · 글 published_at · 미분양 갱신일. */
export function sortByDateDesc<T>(items: readonly T[], dateOf: (x: T) => string | Date | null | undefined): T[] {
  return stable(items, (a, b) => {
    const x = msOf(dateOf(a));
    const y = msOf(dateOf(b));
    return x === y ? 0 : x == null ? 1 : y == null ? -1 : y - x;
  });
}
export const sortRedev = sortByDateDesc;
export const sortPosts = sortByDateDesc;
export const sortUnsold = sortByDateDesc;

/** 게이트 키 = KST 'YYYY-MM-DD'(없으면 빈칸). 재개발·글·미분양 공용. */
export const dateSortKey = (v: string | Date | null | undefined): string => kstYmdOf(v ?? null) ?? '';

/** 재개발 행 라벨 「10.14 변동」(KST). 못 읽으면 null. */
export function stageChangedLabel(stamp: string | Date | null | undefined): string | null {
  const d = kstYmdOf(stamp ?? null);
  return d ? `${d.slice(5, 7)}.${d.slice(8, 10)} 변동` : null;
}

/* ───────────── 지역 타일 ───────────── */

/** 현장 수 내림차순(안정 · 수 없음 0 취급). */
export function sortTiles<T>(items: readonly T[], countOf: (x: T) => number | null | undefined): T[] {
  return stable(items, (a, b) => (Number(countOf(b)) || 0) - (Number(countOf(a)) || 0));
}
export const countSortKey = (n: number | null | undefined): string => String(Number(n) || 0);
